import axios from 'axios';
import { startInactivityTimer, stopInactivityTimer } from './activity-tracker';
import { clearPostAuthEntitlements } from '../newui/services/postAuthEntitlements';
import { ensureAnonymousSession, sendAuditEvent } from './auditTelemetry';
import {
  getAuthProvider,
  setAuthProvider,
  clearAuthProvider,
  getEnvAuthProvider,
} from './authProvider';

// ─── JWT claim parsing (provider-aware) ──────────────────────────────────

function decodeJwtPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

/**
 * Returns the JWT exp claim as ms-since-epoch, or null if the token has no
 * usable exp. Used to seed `token_expiry` from the authoritative signed
 * value rather than the provider's expires_in (which can lie).
 */
function jwtExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
  return exp * 1000;
}

/**
 * Normalized identity claims used by the rest of the app.
 * Both Keycloak and WorkOS shapes flow through here so callers don't branch.
 */
function parseKeycloakClaims(payload) {
  const roles = Array.isArray(payload?.realm_access?.roles)
    ? payload.realm_access.roles
    : [];
  return {
    sub: payload?.sub || null,
    email: payload?.email || '',
    name: payload?.name || payload?.preferred_username || '',
    username: payload?.preferred_username || payload?.email || payload?.sub || '',
    role: roles[0] || '',
    roles,
    permissions: [],
    orgId: '',
    provider: 'keycloak',
  };
}

function parseWorkosClaims(payload) {
  const role = typeof payload?.role === 'string' ? payload.role : '';
  const roles = Array.isArray(payload?.roles)
    ? payload.roles
    : (role ? [role] : []);
  const permissions = Array.isArray(payload?.permissions) ? payload.permissions : [];
  const fullName = [payload?.first_name, payload?.last_name].filter(Boolean).join(' ');
  return {
    sub: payload?.sub || null,
    email: payload?.email || '',
    name: payload?.name || fullName || payload?.email || '',
    username: payload?.email || payload?.sub || '',
    role,
    roles,
    permissions,
    orgId: payload?.org_id || '',
    provider: 'workos',
  };
}

export function parseClaims(token) {
  const raw = token || localStorage.getItem('access_token');
  if (!raw) return null;
  const payload = decodeJwtPayload(raw);
  if (!payload) return null;
  // Detect by claim shape — robust even when localStorage provider stamp is
  // missing (legacy sessions or cross-provider toggles in dev).
  if (payload.realm_access || payload.preferred_username) {
    return parseKeycloakClaims(payload);
  }
  if (typeof payload.role === 'string' || Array.isArray(payload.permissions) || typeof payload.org_id === 'string') {
    return parseWorkosClaims(payload);
  }
  // Unknown shape → empty roles, treat as Keycloak-like for back-compat.
  return parseKeycloakClaims(payload);
}

// ─── Refresh ─────────────────────────────────────────────────────────────

function refreshUrlForProvider(provider) {
  const base = import.meta.env.VITE_API_BASE_URL;
  return provider === 'workos'
    ? `${base}/api/auth/workos/refresh`
    : `${base}/api/auth/refresh`;
}

export async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  const provider = getAuthProvider();
  try {
    const response = await axios.post(
      refreshUrlForProvider(provider),
      { refresh_token: refreshToken }
    );

    const newToken = response.data.access_token;
    const newRefreshToken = response.data.refresh_token;
    const expiresIn = response.data.expires_in;
    const now = Date.now();

    localStorage.setItem('access_token', newToken);
    localStorage.setItem('refresh_token', newRefreshToken);
    // Prefer the JWT's `exp` claim over the provider-reported expires_in —
    // WorkOS sometimes omits expires_in (or echoes a default that doesn't
    // match the actual signed token TTL), causing the proactive refresh in
    // axios-config to miss the window and leaving 401-loops on Sidebar polls.
    const jwtExpMs = jwtExpiryMs(newToken);
    localStorage.setItem(
      'token_expiry',
      jwtExpMs ?? (expiresIn ? now + expiresIn * 1000 : now + 15 * 60 * 1000),
    );

    return newToken;
  } catch (err) {
    sendAuditEvent({ eventName: 'session_refresh_failed', outcome: 'failure' });
    return null;
  }
}

// ─── Token storage ───────────────────────────────────────────────────────

export function storeTokens(data, options = {}) {
  const token = data.access_token;
  const refreshToken = data.refresh_token;
  const expiresIn = data.expires_in;
  const now = Date.now();

  localStorage.setItem('access_token', token);
  localStorage.setItem('refresh_token', refreshToken);
  // Same reasoning as refreshAccessToken — the JWT exp claim is authoritative.
  const jwtExpMs = jwtExpiryMs(token);
  localStorage.setItem(
    'token_expiry',
    jwtExpMs ?? (expiresIn ? now + expiresIn * 1000 : now + 15 * 60 * 1000),
  );

  // Stamp provider so refresh/sign-out know which path to call. Caller can
  // override (e.g. WorkOS callback page); otherwise infer from claim shape.
  const provider = options.provider
    || parseClaims(token)?.provider
    || getEnvAuthProvider();
  setAuthProvider(provider);

  const claims = parseClaims(token);
  if (claims?.role) {
    localStorage.setItem('user_role', claims.role);
  }

  sendAuditEvent({ eventName: 'login_success', outcome: 'success' });
  initInactivityTimer();
}

// ─── Role / identity accessors ───────────────────────────────────────────

/**
 * Return the user's roles in a provider-neutral way.
 * - Keycloak: `realm_access.roles`
 * - WorkOS:   `[role]` (single org-scoped role) plus any `roles` array
 */
export function getRoles() {
  const claims = parseClaims();
  return claims?.roles ?? [];
}

/** @deprecated Use getRoles(). Kept for back-compat with Keycloak callers. */
export function getRealmRoles() {
  return getRoles();
}

export function getPermissions() {
  return parseClaims()?.permissions ?? [];
}

export function getOrgId() {
  return parseClaims()?.orgId || '';
}

export function getCurrentUserId() {
  return parseClaims()?.sub || null;
}

export function isBrokerOrAdmin() {
  const roles = getRoles();
  // Accept both the legacy Keycloak names and the WorkOS org-role names so
  // gating logic works during the dual-provider transition.
  const allowed = new Set([
    'broker-admin',
    'administrators',
    'org_owner',
    'org_admin',
  ]);
  return roles.some((r) => allowed.has(r));
}

// ─── Session lifecycle ───────────────────────────────────────────────────

export function isTokenExpired() {
  const expiry = parseInt(localStorage.getItem('token_expiry') || '0', 10);
  return expiry !== 0 && Date.now() >= expiry;
}

export function isAuthenticated() {
  const token = localStorage.getItem('access_token');
  if (!token) return false;
  return !isTokenExpired();
}

export function clearAuthStorage() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_expiry');
  localStorage.removeItem('remember_me');
  localStorage.removeItem('user_role');
  localStorage.removeItem('workos_session_id');
  clearAuthProvider();
  delete axios.defaults.headers.common['Authorization'];
  clearPostAuthEntitlements();
}

export async function logoutUser() {
  sendAuditEvent({ eventName: 'logout_clicked', outcome: 'success' });

  // Snapshot the provider + WorkOS session id BEFORE we clear local
  // storage — clearAuthStorage() wipes both.
  const provider = getAuthProvider();
  const workosSessionId = localStorage.getItem('workos_session_id');
  const loggedOutUrl = `${window.location.origin}/logged-out`;

  // Ask the backend for a hosted-logout URL while the access token is
  // still valid (so the request can be audited against the real user).
  // The hosted URL ends WorkOS's AuthKit session cookie; without it the
  // next visit to /login silently re-authenticates the same user.
  let workosLogoutUrl = null;
  if (provider === 'workos') {
    try {
      const params = workosSessionId
        ? { session_id: workosSessionId, return_to: loggedOutUrl }
        : undefined;
      const { data } = await axios.post('/api/auth/workos/logout', null, { params });
      if (data && typeof data.logout_url === 'string') {
        workosLogoutUrl = data.logout_url;
      }
    } catch {
      // Network / 4xx / WorkOS SDK error — fall through to the local
      // /logged-out page. AuthKit's cookie may survive, but at least we
      // don't leave the user stranded on a broken page.
    }
  }

  const theme = localStorage.getItem('theme');
  clearAuthStorage();
  if (theme) {
    localStorage.setItem('theme', theme);
  }
  stopInactivityTimer();

  // WorkOS hosted logout → redirects back to /logged-out via return_to.
  // Keycloak / unknown providers → keep the historical /login behaviour
  // so existing flows aren't disturbed.
  if (workosLogoutUrl) {
    window.location.assign(workosLogoutUrl);
    return;
  }
  if (provider === 'workos') {
    window.location.assign(loggedOutUrl);
    return;
  }
  window.location.href = '/login';
}

export function initInactivityTimer(timeoutMinutes = 15, warningSeconds = 30) {
  ensureAnonymousSession();
  startInactivityTimer(
    () => {
      sendAuditEvent({ eventName: 'session_expired', outcome: 'failure' });
      logoutUser();
    },
    timeoutMinutes,
    warningSeconds,
    { tokenRefreshFn: refreshAccessToken, refreshThresholdSeconds: 60 }
  );
}

export function logoutIfExpired() {
  if (isTokenExpired()) {
    logoutUser();
    return true;
  }
  return false;
}
